import React, { Fragment, FunctionComponent, ReactNode, RefObject, useEffect, useMemo, useRef, useState } from "react";

import { Measurer } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { bemClassNames, combineRefs, getMinSize } from "@multiweb/reactUtils";
import { PopupPosition } from "@multiweb/reactUtils/hooks/usePopupFlipHook";
import { IComponentSize } from "@multiweb/types/react";
import { Popup } from "UI/UI.React/components/Popup";
import { useMaxSizes } from "../../hooks";
import { DropdownLoadingProps, DropdownSizesProps } from "../../types";
import { DropdownContainer } from "../DropdownContainer";

const { generateUUID } = bridge;

const className = bemClassNames("dropdown-component-content");

export type DropdownComponentProps = {
    /** Reference na spouštěč popupu. Předává se, aby popup věděl, kde se nachází trigger a mohl podle toho umístit Popup. */
    triggerElement: RefObject<any>;
    /** Reference na obsah popupu. Předává se, aby popup věděl, jak velký je jeho obsah a podle toho ho mohl umístit. */
    contextElement: RefObject<any>;
    /** Požadovaná preferovaná pozice. */
    requestedPosition?: PopupPosition;
    /**
     * Potomci k vyrenderování.
     * Měly by to být být DropdownItem, DropdownListItem,
     * DropdownMultiButtonsItem nebo DropdownSeparatorItem.
     */
    children: ReactNode[];
    /** Určuje stav otevření Dropdownu (Popupu s DropdownContainerem) */
    isOpen: boolean;
} & DropdownSizesProps & DropdownLoadingProps;

/**
 * Komponenta sloužící k vyrenderování Dropdown.
 * O stav otevření se musí starat nadřazená komponenta (@see Dropdown ).
 */
export const DropdownComponent: FunctionComponent<DropdownComponentProps> = ({
    triggerElement,
    contextElement,
    requestedPosition,
    isOpen,
    children,
    isLoading = false,
    loaderRenderer,
    height,
    maxHeight,
    width,
    maxWidth,
}) => {
    const contentOverflowRef = useRef<HTMLDivElement>(null);
    const combinedContextRef = combineRefs(contentOverflowRef, contextElement);
    const [size, setSize] = useState<IComponentSize>();
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    /**
     * Hook vypočte maximální výšku a šířku dropdown contentu na základě určené polohy vůči spouštěcímu elementu.
     */
    const maxSizes = useMaxSizes(triggerElement, isInitialized, size);

    const popupContent = useMemo(() => {
        return <div ref={combinedContextRef} className={className()}>
            <DropdownContainer {...{ isLoading, loaderRenderer, height, width }}
                maxHeight={getMinSize(maxHeight, `calc(${maxSizes?.height}px - var(--base-2))`)}
                maxWidth={getMinSize(maxWidth, `calc(${maxSizes?.width}px  - var(--base-4))`)}
            >
                {children}
            </DropdownContainer>
        </div>;
    // Do dependencies nebyl zahrnut "combinedContextRef" z důvodu vyvolání stálého re-renderu
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxSizes, isLoading, loaderRenderer, height, width, children, maxHeight, maxWidth]);

    const itemsToMeasure = useMemo(() => {
        return [{
            key: generateUUID(),
            component: popupContent,
            onItemMeasured: (size: IComponentSize) => setSize(size),
        }];
    }, [popupContent]);

    useEffect(() => {
        setIsInitialized(false);
    }, [maxSizes?.height, maxSizes?.width]);

    return (
        <Fragment>
            { !isInitialized && <Measurer itemsToMeasure={itemsToMeasure} onAllMeasuredCallback={() => setIsInitialized(true)} /> }
            <Popup {...{ opened: isOpen, requestedPosition, triggerElement, height: size?.height, width: size?.width }}>
                {popupContent}
            </Popup>
        </Fragment>
    );
};
